const REACT_APP_BFF_URL = process.env.REACT_APP_BFF_URL;

/**
 * Get all landing page images
 * @param {string} token - Authentication token
 * @returns {Promise<Array>} Array of landing page images
 */
export const getLandingPageImages = async (token) => {
  const response = await fetch(`${REACT_APP_BFF_URL}/landingpage-images`, {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`,
    }
  });

  if (!response.ok) {
    throw new Error('Error al obtener las imágenes de la página de inicio.');
  }

  return await response.json();
};

/**
 * Get a specific landing page image by ID
 * @param {string} token - Authentication token
 * @param {string} imageId - ID of the image to retrieve
 * @returns {Promise<Object>} Landing page image data
 */
export const getLandingPageImage = async (token, imageId) => {
  const response = await fetch(`${REACT_APP_BFF_URL}/landingpage-images/${imageId}`, {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`
    }
  });

  if (!response.ok) {
    throw new Error('Error al obtener la imagen de la página de inicio.');
  }

  return await response.json();
};

/**
 * Create a new landing page image using FormData
 * @param {string} token - Authentication token
 * @param {File} file - Image file to upload
 * @param {number} [position] - Optional position for ordering
 * @returns {Promise<Object>} Response with created image ID
 */
export const createLandingPageImage = async (token, file, position = 0) => {
  const formData = new FormData();
  formData.append('file', file);
  formData.append('position', position.toString());

  const response = await fetch(`${REACT_APP_BFF_URL}/landingpage-images`, {
    method: 'POST',
    headers: {
      'Authorization': `Bearer ${token}`
    },
    body: formData,
  });

  if (!response.ok) {
    throw new Error('Error al crear la imagen de la página de inicio.');
  }

  return await response.json();
};

/**
 * Update an existing landing page image using FormData
 * @param {string} token - Authentication token
 * @param {string} imageId - ID of the image to update
 * @param {File} [file] - New image file (optional)
 * @param {number} [position] - Position for ordering
 * @returns {Promise<Object>} Response with update confirmation
 */
export const updateLandingPageImage = async (token, imageId, file = null, position = null) => {
  const formData = new FormData();
  
  if (file) {
    formData.append('file', file);
  }
  
  if (position !== null) {
    formData.append('position', position.toString());
  }
  
  const response = await fetch(`${REACT_APP_BFF_URL}/landingpage-images/${imageId}`, {
    method: 'PUT',
    headers: {
      'Authorization': `Bearer ${token}`
    },
    body: formData,
  });

  if (!response.ok) {
    throw new Error('Error al actualizar la imagen de la página de inicio.');
  }

  return await response.json();
};

/**
 * Delete a landing page image
 * @param {string} token - Authentication token
 * @param {string} imageId - ID of the image to delete
 * @returns {Promise<Object>} Response with deletion confirmation
 */
export const deleteLandingPageImage = async (token, imageId) => {
  const response = await fetch(`${REACT_APP_BFF_URL}/landingpage-images/${imageId}`, {
    method: 'DELETE',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`
    }
  });

  if (!response.ok) {
    throw new Error('Error al eliminar la imagen de la página de inicio.');
  }

  return await response.json();
};

/**
 * Update only the position of a landing page image
 * @param {string} token - Authentication token
 * @param {string} imageId - ID of the image to update
 * @param {number} position - New position value
 * @returns {Promise<Object>} Response with update confirmation
 */
export const updateLandingPageImagePosition = async (token, imageId, position) => {
  const response = await fetch(`${REACT_APP_BFF_URL}/landingpage-images/${imageId}/position`, {
    method: 'PATCH',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`
    },
    body: JSON.stringify({
      position: position
    }),
  });

  if (!response.ok) {
    throw new Error('Error al actualizar la posición de la imagen.');
  }

  return await response.json();
};

/**
 * Reorder all landing page images to have sequential positions
 * @param {string} token - Authentication token
 * @returns {Promise<Object>} Response with reordered images
 */
export const reorderLandingPageImages = async (token) => {
  const response = await fetch(`${REACT_APP_BFF_URL}/landingpage-images/reorder`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`
    }
  });

  if (!response.ok) {
    throw new Error('Error al reordenar las imágenes de la página de inicio.');
  }

  return await response.json();
};

/**
 * Convert a file to base64 string (kept for backward compatibility)
 * @param {File} file - File to convert
 * @returns {Promise<string>} Promise with base64 string
 */
export const fileToBase64 = (file) => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = () => {
      if (typeof reader.result === 'string') {
        // Remove the data URL prefix (e.g., "data:image/jpeg;base64,")
        const base64String = reader.result.split(',')[1];
        resolve(base64String);
      } else {
        reject(new Error('Failed to convert file to base64'));
      }
    };
    reader.onerror = error => reject(error);
  });
};